/**
 * useStreamSubmission — owns the entire SSE stream lifecycle for user-response streams.
 *
 * All three submission paths (text response, form submit, retry) share the same
 * fetchEventSource setup and SSE event handling. This hook centralises that logic so
 * changes to stream behaviour only need to happen in one place.
 *
 * SSE event flow:
 *   message_start         — actual ticket status/role before processing begins (replaces optimistic update)
 *   current_action        — real-time tool progress text (current_action field)
 *   last_output           — one item appended to last_output (accumulated by frontend)
 *   log_event             — execution entry captured; streamed to InfoSidePanel in real time
 *   thread_chunk_start    — metadata for the next streamed message (id, role, timestamp)
 *   thread_chunk          — one streamed LLM token
 *   thread_chunk_end      — streamed message complete; commit it to the conversation
 *   thread_message        — non-streamed message injected directly (system, form replacement)
 *   child_ticket_created  — child ticket created via hand_over_to_agent()
 *   message_complete      — stream finished; final status, role, and ticket_data for reconciliation
 *   error                 — processing failed
 *
 * Usage:
 *   const { openStream } = useStreamSubmission({ ... })
 *
 *   // In a handler's pre-flight:
 *   pausePolling('message submission')
 *   setPendingMessage(pendingMsg)
 *   setLastAgentMessageCount(agentMsgCount)   // snapshot before stream opens
 *   await openStream({ body: { response: text, user_email: user.email } })
 *
 * Sequential event pipeline:
 *   Three event types are enqueued as items arrive:
 *     { type: 'turn',     buffer, fullContent, endData }  — an LLM turn to typewrite
 *     { type: 'message',  id, role, content, ... }        — a thread_message to inject
 *     { type: 'complete', status, role }                  — signals end of stream
 *
 *   processNext() is the single processor. It handles items one at a time:
 *     'message'  — inject immediately, shift, call processNext() to continue
 *     'turn'     — start the typewriter timer; do NOT advance until the buffer is fully
 *                  drained AND llm_chunk_end has arrived (endData set). commitAndAdvance()
 *                  injects the message then calls processNext() for the next item.
 *     'complete' — final cleanup, close connection
 *
 *   Two other event types are NOT queued — they mutate state in-place:
 *     thread_chunk      — appended directly to activeInputTurnRef (the last 'turn' pushed)
 *     thread_chunk_end  — sets endData on activeInputTurnRef; if the turn is head and its
 *                      buffer is already empty, calls commitAndAdvance() immediately.
 *
 *   processNext() is only called when:
 *     - A new item is pushed and the queue was previously empty (kick-off)
 *     - commitAndAdvance() finishes a turn (advance to next item)
 *     - A message is injected (advance to next item)
 *   This ensures items are processed strictly in order, one at a time.
 */

import { useRef, useCallback } from 'react'
import { fetchEventSource } from '@microsoft/fetch-event-source'
import { buildSSEUrl, getAccessToken } from '../services/api'
import { isDirectOwnerInteraction } from '../utils/conversationPolling'

const CHARS_PER_TICK = 3
const RENDER_INTERVAL_MS = 16
const WATCHDOG_CHECK_INTERVAL_MS = 10_000 // poll for timeout every 10s
const WATCHDOG_TIMEOUT_MS = 45_000        // 45s > 30s server heartbeat interval
// For direct owner+ticket (non-grouped), delay polling after each turn so we don't poll
// while the user is actively reading/typing. Resets on every submission via pausePolling().
const LONG_RESUME_DELAY_MS = 30_000

export function useStreamSubmission({
  displayTicket,
  user,
  // Refs
  streamAbortRef,
  renderTimerRef,
  chunkBufferRef,
  fullStreamedContentRef,
  // State setters (stable React guarantees — no need to list in deps)
  setSubmitting,
  setImmediateThinking,
  setDirectStreamContent,
  setStreamingAction,
  setStreamingOutputs,
  setStreamingLogs,
  setLastAgentMessageCount,
  setPendingMessage,
  // Parent callbacks
  onImmediateThinkingChange,
  setThinkingPhrase,
  getRandomThinkingPhrase,
  // Polling & injection (from useTicketDetails)
  resumePolling,
  resumePollingAfterDelay,
  triggerImmediatePoll,
  injectOptimisticMessage,
  injectTicketStatusUpdate,
  injectChildTicket,
  injectTicketDataUpdate,
}) {
  // Sequential event pipeline. Items:
  //   { type: 'turn',     buffer: string, fullContent: string, endData: object|null }
  //   { type: 'message',  id, role, content, messageMetadata, timestamp }
  //   { type: 'complete', status, role }
  const queueRef = useRef([])

  // Reference to the 'turn' item currently receiving llm_chunk tokens (the last turn pushed).
  // Mutations to this ref also update the queue item (JS pass-by-reference).
  const activeInputTurnRef = useRef(null)

  // Heartbeat watchdog: tracks the last time any SSE event was received.
  // If no event arrives within WATCHDOG_TIMEOUT_MS, the connection is considered dead.
  const lastEventTimeRef = useRef(null)
  const watchdogTimerRef = useRef(null)

  /**
   * Clean up all streaming state and mark the pending message as failed.
   * Called on SSE error events and network errors.
   */
  const handleStreamError = useCallback((errorMessage) => {
    if (watchdogTimerRef.current) {
      clearInterval(watchdogTimerRef.current)
      watchdogTimerRef.current = null
    }
    if (renderTimerRef.current) {
      clearInterval(renderTimerRef.current)
      renderTimerRef.current = null
    }
    chunkBufferRef.current = ''
    fullStreamedContentRef.current = ''
    queueRef.current = []
    activeInputTurnRef.current = null
    setPendingMessage(prev => prev ? { ...prev, pending: false, error: true, errorMessage } : null)
    setDirectStreamContent('')
    setStreamingAction('')
    setStreamingOutputs([])
    setImmediateThinking(false)
    onImmediateThinkingChange(false)
    resumePolling()
    // Immediately fetch the current ticket state so that if the agent already responded
    // while the stream was disconnected, the response shows without requiring a retry.
    triggerImmediatePoll()
    setSubmitting(false)
  }, [
    renderTimerRef, chunkBufferRef, fullStreamedContentRef,
    setPendingMessage, setDirectStreamContent, setStreamingAction, setStreamingOutputs, setStreamingLogs,
    setImmediateThinking, onImmediateThinkingChange, resumePolling, triggerImmediatePoll, setSubmitting,
  ])

  /**
   * Open the SSE stream for the current ticket.
   *
   * @param {object} options
   * @param {object} options.body               - JSON body for the POST (e.g. { response, user_email })
   * @param {string} [options.defaultErrorMessage] - Fallback message when the server doesn't provide one
   */
  const openStream = useCallback(async ({
    body,
    formData,
    url,
    defaultErrorMessage = 'Failed to submit response. Please try again.',
    onOpen,
  }) => {
    // Abort any in-flight stream and create a fresh controller.
    // Also clear any timer that may still be running from the previous stream — the
    // AbortError path in .catch() skips handleStreamError, so the timer is not cleared
    // there. Without this, startTypewriterTimer() would be a no-op for the new stream.
    if (streamAbortRef.current) streamAbortRef.current.abort()
    if (renderTimerRef.current) {
      clearInterval(renderTimerRef.current)
      renderTimerRef.current = null
    }
    if (watchdogTimerRef.current) {
      clearInterval(watchdogTimerRef.current)
      watchdogTimerRef.current = null
    }
    const abortController = new AbortController()
    streamAbortRef.current = abortController
    chunkBufferRef.current = ''
    fullStreamedContentRef.current = ''
    queueRef.current = []
    activeInputTurnRef.current = null

    // Start the heartbeat watchdog. The server emits a heartbeat every 30s; if no event
    // arrives within 45s the connection is assumed frozen (e.g. machine went to sleep).
    lastEventTimeRef.current = Date.now()
    watchdogTimerRef.current = setInterval(() => {
      if (Date.now() - lastEventTimeRef.current > WATCHDOG_TIMEOUT_MS) {
        clearInterval(watchdogTimerRef.current)
        watchdogTimerRef.current = null
        abortController.abort()
        handleStreamError('Connection lost — please try again.')
      }
    }, WATCHDOG_CHECK_INTERVAL_MS)

    setSubmitting(true)
    setImmediateThinking(true)
    onImmediateThinkingChange(true)
    setThinkingPhrase('Sending')

    let token = ''
    try {
      token = await getAccessToken()
    } catch (e) {
      console.error('Failed to get access token for stream:', e)
    }

    const streamUrl = url ?? buildSSEUrl(`/tickets/${displayTicket.id}/user-response-stream`)

    // ─── commitAndAdvance ──────────────────────────────────────────────────────
    // Called when the head turn's typewriter finishes (buffer drained + endData set).
    // Injects the turn message, resets streaming UI state, then calls processNext()
    // to hand off to the next item in the queue.
    const commitAndAdvance = () => {
      const head = queueRef.current[0]
      if (!head || head.type !== 'turn') return

      if (renderTimerRef.current) {
        clearInterval(renderTimerRef.current)
        renderTimerRef.current = null
      }

      queueRef.current.shift()

      if (head.fullContent) {
        injectOptimisticMessage({
          id: head.endData?.id || undefined,
          role: head.endData?.role || 'assistant',
          content: head.fullContent,
          messageMetadata: head.endData?.messageMetadata || {},
          timestamp: head.endData?.timestamp || new Date().toISOString(),
          userEmail: head.endData?.userEmail || undefined,
          userName: head.endData?.userName || undefined,
        })
        setLastAgentMessageCount(prev => prev + 1) // prevent auto-clear of thinking bubble
      }

      setDirectStreamContent('')
      setStreamingAction('')
      setStreamingOutputs([])

      processNext()
    }

    // ─── processNext ──────────────────────────────────────────────────────────
    // The sequential processor. Called only when an item finishes or when the first
    // item is pushed to a previously-empty queue.
    //
    //   'message'  — inject immediately, shift, recurse to continue draining messages
    //   'turn'     — start (or resume) the typewriter; return and wait for it to finish
    //   'complete' — final cleanup and close connection
    const processNext = () => {
      const head = queueRef.current[0]
      if (!head) return

      if (head.type === 'message') {
        queueRef.current.shift()
        injectOptimisticMessage({
          id: head.id,
          role: head.role,
          content: head.content,
          messageMetadata: head.messageMetadata,
          timestamp: head.timestamp,
          userEmail: head.userEmail || undefined,
          userName: head.userName || undefined,
        })
        processNext() // drain consecutive messages before yielding

      } else if (head.type === 'turn') {
        if (head.buffer) {
          startTypewriterTimer() // drain whatever has accumulated; more chunks may still arrive
        } else if (head.endData) {
          commitAndAdvance() // no content to typewrite — commit and move on
        }
        // else: waiting for llm_chunk events; startTypewriterTimer() called when they arrive

      } else if (head.type === 'complete') {
        queueRef.current.shift()
        if (watchdogTimerRef.current) {
          clearInterval(watchdogTimerRef.current)
          watchdogTimerRef.current = null
        }
        if (renderTimerRef.current) {
          clearInterval(renderTimerRef.current)
          renderTimerRef.current = null
        }
        setDirectStreamContent('')
        setStreamingAction('')
        setStreamingOutputs([])
        if (head.status || head.role) injectTicketStatusUpdate(head.status, head.role)
        setImmediateThinking(false)
        onImmediateThinkingChange(false)
        // Direct owner in a non-grouped ticket: use a long delay so polling doesn't fire
        // while they're reading/composing. Each new submission resets the timer via pausePolling().
        // All other cases (grouped, watcher) resume immediately so observers stay in sync.
        if (isDirectOwnerInteraction(displayTicket, user?.email)) {
          resumePollingAfterDelay(LONG_RESUME_DELAY_MS)
        } else {
          resumePolling()
          triggerImmediatePoll()
        }
        setSubmitting(false)
        abortController.abort() // cleanly close the SSE connection
      }
    }

    // ─── Typewriter timer ──────────────────────────────────────────────────────
    // Drains the head turn's buffer 4 chars at a time.
    // Calls commitAndAdvance() when buffer is empty and llm_chunk_end has arrived.
    // If buffer empties before llm_chunk_end, the timer keeps firing until endData is set.
    const startTypewriterTimer = () => {
      if (renderTimerRef.current) return
      renderTimerRef.current = setInterval(() => {
        const head = queueRef.current[0]
        if (!head || head.type !== 'turn') return

        const chunk = head.buffer.slice(0, CHARS_PER_TICK)
        if (!chunk) {
          // Buffer empty — commit only once llm_chunk_end has provided endData.
          if (head.endData) commitAndAdvance()
          return
        }
        head.buffer = head.buffer.slice(CHARS_PER_TICK)
        setDirectStreamContent(prev => prev + chunk)
      }, RENDER_INTERVAL_MS)
    }

    fetchEventSource(streamUrl, {
      method: 'POST',
      headers: {
        ...(formData ? {} : { 'Content-Type': 'application/json' }),
        Authorization: `Bearer ${token}`,
      },
      body: formData ?? JSON.stringify(body),
      signal: abortController.signal,
      openWhenHidden: true,

      onopen(res) {
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        // Connection established — switch from "Sending" to a thinking phrase
        setThinkingPhrase(getRandomThinkingPhrase())
        onOpen?.()
        setPendingMessage(prev => prev ? { ...prev, pending: false } : null)
      },

      onmessage(msg) {
        // Reset watchdog on every event, including heartbeats — they prove the connection is alive.
        lastEventTimeRef.current = Date.now()
        if (!msg.event || msg.event === 'heartbeat') return
        let data = {}
        try { data = JSON.parse(msg.data) } catch { /* non-JSON data */ }

        if (msg.event === 'message_start') {
          // Server confirms the actual ticket status/role at stream open — no guessing needed.
          if (data.status || data.role) injectTicketStatusUpdate(data.status, data.role)

        } else if (msg.event === 'thread_chunk_start') {
          // Abandon any previous active turn with no content and no end signal —
          // tool-call LLM turns fire thread_chunk_start but emit no tokens or thread_chunk_end,
          // leaving an orphaned turn that permanently blocks the queue.
          const prevTurn = activeInputTurnRef.current
          let queueUnblocked = false
          if (prevTurn && !prevTurn.fullContent && !prevTurn.endData) {
            const idx = queueRef.current.indexOf(prevTurn)
            if (idx !== -1) {
              queueRef.current.splice(idx, 1)
              queueUnblocked = true
            }
          }
          // Push a new turn item. activeInputTurnRef points at the same object so
          // thread_chunk / thread_chunk_end mutations update the queue item in place.
          const newTurn = { type: 'turn', buffer: '', fullContent: '', endData: null }
          const wasEmpty = queueRef.current.length === 0
          queueRef.current.push(newTurn)
          activeInputTurnRef.current = newTurn
          setStreamingAction('') // clear any inter-turn action text
          if (wasEmpty || queueUnblocked) processNext() // kick off the processor if queue was idle or just unblocked

        } else if (msg.event === 'thread_chunk') {
          // Append to the active input turn (NOT queued — mutates the turn item in place).
          // Only start the timer if this turn is currently at the head (being rendered).
          // If it is queued behind another turn, startTypewriterTimer() is called at promotion.
          const turn = activeInputTurnRef.current
          if (!turn) return
          turn.buffer += (data.content || '')
          turn.fullContent += (data.content || '')
          if (queueRef.current[0] === turn) {
            startTypewriterTimer()
          }

        } else if (msg.event === 'thread_chunk_end') {
          // Set endData on the active input turn (NOT queued — mutates in place).
          // If this turn is already at the head and its buffer is empty (all chunks
          // drained by the timer before end arrived), commit immediately.
          const turn = activeInputTurnRef.current
          if (!turn) return
          turn.endData = {
            id: data.id || undefined,
            role: data.role || 'assistant',
            messageMetadata: data.messageMetadata || {},
            timestamp: data.timestamp || new Date().toISOString(),
            userEmail: data.userEmail || undefined,
            userName: data.userName || undefined,
          }
          if (queueRef.current[0] === turn && !turn.buffer) {
            commitAndAdvance()
          }

        } else if (msg.event === 'thread_message') {
          // Enqueue in arrival order. processNext() will inject this message after the
          // preceding turn commits and before the next turn starts.
          const wasEmpty = queueRef.current.length === 0
          queueRef.current.push({
            type: 'message',
            id: data.id || undefined,
            role: data.role,
            content: data.content,
            messageMetadata: data.metadata || {},
            timestamp: data.timestamp || new Date().toISOString(),
            userEmail: data.userEmail || undefined,
            userName: data.userName || undefined,
          })
          if (wasEmpty) processNext()

        } else if (msg.event === 'message_complete') {
          // Enqueue so cleanup happens in order, after all preceding turns and messages.
          if (data.ticket_data) injectTicketDataUpdate(data.ticket_data)
          const wasEmpty = queueRef.current.length === 0
          queueRef.current.push({ type: 'complete', status: data.status, role: data.role })
          if (wasEmpty) processNext()

        } else if (msg.event === 'current_action') {
          // Payload key matches TicketResponse.current_action
          setStreamingAction(data.current_action || '')

        } else if (msg.event === 'last_output') {
          // Payload key matches TicketResponse.last_output; accumulated into an array here
          setStreamingOutputs(prev => [...prev, data.last_output || ''])

        } else if (msg.event === 'log_event') {
          // ExecutionEntry-shaped object; append to streamingLogs for real-time InfoSidePanel display
          setStreamingLogs(prev => [...prev, data])

        } else if (msg.event === 'child_ticket_created') {
          // Inject child ticket so subsequent streamed messages route correctly
          injectChildTicket(data)

        } else if (msg.event === 'error') {
          handleStreamError(data.message || 'Failed to process response. Please try again.')
          abortController.abort()
        }
      },

      onerror(err) {
        // Always throw to prevent fetchEventSource from auto-reconnecting
        throw err
      },
    }).catch((err) => {
      if (err.name === 'AbortError') return // clean abort — no action needed
      console.error('Stream connection failed:', err)
      let errorMessage = defaultErrorMessage
      if (err.message?.includes('HTTP 403')) errorMessage = 'Access denied. You can only respond to tickets you\'re involved with.'
      else if (err.message?.includes('HTTP 404')) errorMessage = 'Ticket not found.'
      handleStreamError(errorMessage)
    })
  }, [
    displayTicket,
    user,
    streamAbortRef, renderTimerRef, chunkBufferRef, fullStreamedContentRef,
    setSubmitting, setImmediateThinking, onImmediateThinkingChange, setThinkingPhrase,
    getRandomThinkingPhrase, setDirectStreamContent, setStreamingAction, setStreamingOutputs, setStreamingLogs,
    setLastAgentMessageCount, setPendingMessage,
    resumePolling, resumePollingAfterDelay, triggerImmediatePoll, injectOptimisticMessage, injectTicketStatusUpdate,
    injectChildTicket, injectTicketDataUpdate,
    handleStreamError,
  ])

  return { openStream }
}
